import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { API_BASE_URL } from './api-config';
import { AuthService } from './auth.service';

const THEMED_PROPERTIES = ['--accent', '--accent-ink', '--accent-soft'] as const;

/** Blends a "#rrggbb" hex color toward white by `ratio` (0 = unchanged, 1 = white). */
export function lightenHex(hex: string, ratio: number): string {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) {
    return hex;
  }
  const toHex = (channel: number) => Math.round(channel + (255 - channel) * ratio).toString(16).padStart(2, '0');
  const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Swaps the Invento Design System's brand custom properties (styles.scss) and the header's logo mark for
 * the current user's Organization branding (Azure Boards #9236), falling back to the Invento defaults when
 * the org hasn't set its own. Colors ride along on login (AuthService.currentBranding); the logo image is
 * fetched separately since it's binary — via HttpClient (so the auth interceptor attaches the bearer token)
 * rather than a plain <img src>, since the branding logo route requires authentication.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  readonly logoUrl = signal<string | null>(null);
  private objectUrl: string | null = null;

  constructor(private http: HttpClient, private auth: AuthService) {}

  applyForCurrentUser(): void {
    const branding = this.auth.currentBranding();
    this.applyColors(branding?.primaryColor ?? null, branding?.secondaryColor ?? null);
    if (branding?.hasLogo) {
      this.loadLogo();
    } else {
      this.clearLogo();
    }
  }

  reset(): void {
    this.applyColors(null, null);
    this.clearLogo();
  }

  private applyColors(primaryColor: string | null, secondaryColor: string | null): void {
    const root = document.documentElement.style;
    if (!primaryColor) {
      THEMED_PROPERTIES.forEach((property) => root.removeProperty(property));
      return;
    }
    root.setProperty('--accent', primaryColor);
    root.setProperty('--accent-ink', secondaryColor ?? primaryColor);
    root.setProperty('--accent-soft', lightenHex(primaryColor, 0.85));
  }

  private loadLogo(): void {
    this.http.get(`${API_BASE_URL}/branding/logo`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        this.revokeObjectUrl();
        this.objectUrl = URL.createObjectURL(blob);
        this.logoUrl.set(this.objectUrl);
      },
      error: () => this.clearLogo()
    });
  }

  private clearLogo(): void {
    this.revokeObjectUrl();
    this.logoUrl.set(null);
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
